import React from 'react';
import { Modal } from 'react-bootstrap';
import { AddIcon, EditIcon, TrashIcon, ViewIcon } from 'assets/icons';
import { Button, Input, Pagination } from 'components';
import { Constants, Globals } from 'utils';
import { ModalDisablePlan, ModalDeletePlan, ModalEnablePlan } from './modal-confirm';
import ModalViewPlan from './modal-view-plan';
import ModalEditPlan from './modal-edit-plan';
import { PlansService } from 'services';

type IonChangeEvent = React.ChangeEvent<HTMLInputElement>;

const MODAL = {
  NONE: 0,
  VIEW: 1,
  CREATE: 2,
  EDIT: 3,
  ENABLE: 4,
  DISABLE: 5,
  DELETE: 6,
};

const getStatusColor = (status: number) => {
  switch(status) {
    case Constants.USER.VERIFIED.PENDING:
      return { className: 'pending', label: 'Sin validar' };
    case Constants.USER.VERIFIED.VERIFIED:
      return { className: 'active', label: 'Aprobado' };
    case Constants.USER.VERIFIED.REJECTED:
      return { className: 'rejected', label: 'Rechazado' };
    case Constants.USER.VERIFIED.IN_PROCESS:
      return { className: 'pending', label: 'Pendiente' };
    default:
      return { className: 'rejected', label: 'No definido' };
  }
}

const initialForm = {
  search: '',
  date: '',
  level_id: Constants.LEVELS.PATIENT,
};

class Plans extends React.Component {

  state = {
    modal: MODAL.NONE,
    selectedItem: null,
    form: { ...initialForm },
    data: [],
    pagination: {
      pages: 1,
      page: 1,
    },
  }

  debounce?: NodeJS.Timeout;

  componentDidMount(): void {
    this.getPlans();
  }

  getPlans = async (page = 1) => {
    try {
      const res = await PlansService.get({ ...this.state.form, page });
      const { data, ...rest } = res as any;

      this.setState({
        data: data,
        pagination: rest,
      });

    } catch (error) {
      Globals.showError('Ocurrió un error inesperado');
    }
  }

  change = async (value: string | number | boolean, target: keyof typeof initialForm) => {
    const form = Globals.clone(this.state.form);
    form[target] = value;
    await this.setState({ form });

    if (target === 'level_id') {
      await this.setState({
        data: [],
        pagination: {
          page: 1,
          pages: 1,
        },
      });
      this.getPlans();

    } else {
      if (this.debounce) clearTimeout(this.debounce);
      this.debounce = setTimeout(async () => this.getPlans(), 800);
    }
  }

  cleanFilters = async () => {
    await this.setState((prev: any) => ({
      form: { ...initialForm, level_id: prev.form.level_id },
    }));

    this.getPlans();
  }

  setModal = (modal: number, item?: any) => {
    this.setState({ modal, selectedItem: item ?? null });
  }

  canCleanFilters = () => {
    if (this.state.form.search !== initialForm.search)
      return true;

    if (this.state.form.date !== initialForm.date)
      return true;

    return false
  }

  render() {

    const { form, modal } = this.state;
    const canCleanFilters = this.canCleanFilters();

    return (
      <div id="plans">
        <Modal
          id="plans-view"
          show={modal === MODAL.VIEW}
          onHide={() => this.setModal(MODAL.NONE)}
        >
          <ModalViewPlan
            plan={this.state.selectedItem}
            onClose={() => this.setModal(MODAL.NONE)}
            onEdit={() => this.setModal(MODAL.EDIT, this.state.selectedItem)}
          />
        </Modal>

        <Modal
          id="plans-edit"
          show={modal === MODAL.CREATE}
          onHide={() => this.setModal(MODAL.NONE)}
          size="lg"
        >
          <ModalEditPlan
            onClose={(reload) => {
              this.setModal(MODAL.NONE);
              if (reload) this.getPlans();
            }}
          />
        </Modal>

        <Modal
          id="plans-edit"
          show={modal === MODAL.EDIT}
          onHide={() => this.setModal(MODAL.NONE)}
          size="lg"
        >
          <ModalEditPlan
            plan={this.state.selectedItem}
            onClose={(reload) => {
              this.setModal(MODAL.NONE);
              if (reload) this.getPlans();
            }}
          />
        </Modal>

        <Modal
          id="plans-enable"
          show={modal === MODAL.ENABLE}
          onHide={() => this.setModal(MODAL.NONE)}
        >
          <ModalEnablePlan
            plan={this.state.selectedItem}
            onClose={(reload) => {
              this.setModal(MODAL.NONE);
              if (reload) this.getPlans();
            }}
          />
        </Modal>

        <Modal
          id="plans-disable"
          show={modal === MODAL.DISABLE}
          onHide={() => this.setModal(MODAL.NONE)}
        >
          <ModalDisablePlan
            plan={this.state.selectedItem}
            onClose={(reload) => {
              this.setModal(MODAL.NONE);
              if (reload) this.getPlans();
            }}
          />
        </Modal>

        <Modal
          id="plans-delete"
          show={modal === MODAL.DELETE}
          onHide={() => this.setModal(MODAL.NONE)}
        >
          <ModalDeletePlan
            plan={this.state.selectedItem}
            onClose={(reload) => {
              this.setModal(MODAL.NONE);
              if (reload) this.getPlans();
            }}
          />
        </Modal>

        <section className="filters">
          <div className="main-filter">
            <Input
              className="filter-x2"
              label="Buscar"
              placeholder="Nombre del plan"
              value={form.search}
              onChange={(e: IonChangeEvent) => this.change(e.target.value, 'search')}
            />
          </div>
          {canCleanFilters && (
            <Button clear onClick={this.cleanFilters}>
              Limpiar
            </Button>
          )}
        </section>

        <div className="rg-table">
          <div className="rg-table-header">
            <p></p>
            <div className="rg-table-actions">
              <button onClick={() => this.setModal(MODAL.CREATE)}>
                <img src={AddIcon} alt="Nuevo" />
                <span>Nuevo</span>
              </button>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Costo ($)</th>
                <th>Estatus</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {this.state.data.map((plan: any, index: number) => {
                const status = getStatusColor(plan?.verified);
                const canEnable = plan?.status === Constants.PLAN.STATUS.INACTIVE;
                const canDisable = plan?.status === Constants.PLAN.STATUS.ACTIVE;

                return (
                  <tr key={index}>
                    <td>{ plan?.name }</td>
                    <td>{ Globals.formatMiles(plan?.price_annual, !Number.isInteger(plan?.price_annual), '') }</td>
                    <td>
                      <span className={status?.className}>{ plan?.status_text }</span>
                    </td>
                    <td>
                      <div className="actions">
                        {canDisable && (
                          <span className="badge-primary" onClick={() => this.setModal(MODAL.DISABLE, plan)}>
                            Suspender
                          </span>
                        )}
                        {canEnable && (
                          <span className="badge-secondary" onClick={() => this.setModal(MODAL.ENABLE, plan)}>
                            Activar
                          </span>
                        )}
                        <img
                          src={ViewIcon}
                          alt="Ver"
                          title="Ver"
                          onClick={() => this.setModal(MODAL.VIEW, plan)}
                        />
                        <img
                          src={EditIcon}
                          alt="Editar"
                          title="Editar"
                          onClick={() =>this.setModal(MODAL.EDIT, plan)}
                        />
                        <img
                          src={TrashIcon}
                          alt="Eliminar"
                          title="Eliminar"
                          onClick={() =>this.setModal(MODAL.DELETE, plan)}
                        />
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <Pagination
          pages={this.state.pagination.pages}
          active={this.state.pagination.page}
          onChange={this.getPlans}
        />
      </div>
    )
  }
}

export default Plans;